import React, { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
    prompt(): Promise<void>;
}

interface PWAInstallProps {
    className?: string;
}

const PWAInstall: React.FC<PWAInstallProps> = ({ className = '' }) => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showInstallButton, setShowInstallButton] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        // Check if app is already installed or running in standalone mode
        const checkInstallStatus = () => {
            const isInStandalone = window.matchMedia('(display-mode: standalone)').matches;
            const isInWebAppiOS = (window.navigator as any).standalone === true;
            const isInWebAppChrome = window.matchMedia('(display-mode: minimal-ui)').matches;

            setIsStandalone(isInStandalone || isInWebAppiOS || isInWebAppChrome);
            setIsInstalled(isInStandalone || isInWebAppiOS);
        };

        checkInstallStatus();

        // Listen for beforeinstallprompt event
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            setShowInstallButton(true);
        };

        // Listen for app installed event
        const handleAppInstalled = () => {
            console.log('🎉 Podus AI has been installed!');
            setIsInstalled(true);
            setShowInstallButton(false);
            setDeferredPrompt(null);

            // Track installation
            if ((window as any).gtag) {
                (window as any).gtag('event', 'pwa_install', {
                    event_category: 'PWA',
                    event_label: 'Podus AI Installed',
                });
            }
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;

        try {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;

            if (outcome === 'accepted') {
                console.log('✅ User accepted the install prompt');
                // Track successful prompt acceptance
                if ((window as any).gtag) {
                    (window as any).gtag('event', 'pwa_prompt_accepted', {
                        event_category: 'PWA',
                        event_label: 'Install Prompt Accepted',
                    });
                }
            } else {
                console.log('❌ User dismissed the install prompt');
                // Track prompt dismissal
                if ((window as any).gtag) {
                    (window as any).gtag('event', 'pwa_prompt_dismissed', {
                        event_category: 'PWA',
                        event_label: 'Install Prompt Dismissed',
                    });
                }
            }

            setDeferredPrompt(null);
            setShowInstallButton(false);
        } catch (error) {
            console.error('❌ Error during app installation:', error);
        }
    };

    // Don't show install button if already installed or in standalone mode
    if (isInstalled || isStandalone || !showInstallButton) {
        return null;
    }

    return (
        <div className={`pwa-install ${className}`}>
            <div className="pwa-install-banner">
                <div className="pwa-install-content">
                    <div className="pwa-install-icon">
                        <svg
                            className="w-8 h-8 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                    <div className="pwa-install-text">
                        <h3 className="pwa-install-title">Install Podus AI</h3>
                        <p className="pwa-install-description">
                            Get faster access to legal help with our app. Install now for offline support and instant
                            notifications.
                        </p>
                    </div>
                </div>
                <div className="pwa-install-actions">
                    <button
                        onClick={handleInstallClick}
                        className="pwa-install-button pwa-install-button-primary"
                        aria-label="Install Podus AI app"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                        Install App
                    </button>
                    <button
                        onClick={() => {
                            setShowInstallButton(false);
                            setDeferredPrompt(null);
                        }}
                        className="pwa-install-button pwa-install-button-secondary"
                        aria-label="Dismiss install prompt"
                    >
                        Maybe Later
                    </button>
                </div>
            </div>

            <style>{`
        .pwa-install {
          position: fixed;
          bottom: 20px;
          left: 20px;
          right: 20px;
          z-index: 1000;
          max-width: 400px;
          margin: 0 auto;
        }

        .pwa-install-banner {
          background: #343439;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          color: white;
          animation: slideUp 0.3s ease-out;
        }

        .pwa-install-content {
          display: flex;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .pwa-install-icon {
          margin-right: 12px;
          flex-shrink: 0;
        }

        .pwa-install-text {
          flex: 1;
        }

        .pwa-install-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          line-height: 1.4;
        }

        .pwa-install-description {
          font-size: 14px;
          margin: 0;
          opacity: 0.9;
          line-height: 1.5;
        }

        .pwa-install-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .pwa-install-button {
          display: inline-flex;
          align-items: center;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .pwa-install-button-primary {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          backdrop-filter: blur(10px);
        }

        .pwa-install-button-primary:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }

        .pwa-install-button-secondary {
          background: transparent;
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .pwa-install-button-secondary:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (max-width: 480px) {
          .pwa-install {
            left: 10px;
            right: 10px;
            bottom: 10px;
          }

          .pwa-install-banner {
            padding: 16px;
          }

          .pwa-install-actions {
            flex-direction: column;
          }

          .pwa-install-button {
            justify-content: center;
          }
        }
  `}</style>
        </div>
    );
};

export default PWAInstall;
